import moment from 'moment'
import type {
  ChangeEventHandler,
  FocusEventHandler,
  MouseEventHandler,
} from 'react'
import { useCallback, useImperativeHandle, useRef, useState } from 'react'
import * as React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import RHFInput from './RHFInput'

import IconButton from 'LuxKit/Button/IconButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { SHORT_TIME_FORMAT_24_HOUR, INTL_SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import isSafari from 'lib/web/isSafari'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'

const generateTimes = () => {
  const times: Array<string> = []

  const m = moment().hours(0).minutes(0)

  for (let i = 0; i < 24 * 2; i++) {
    times.push(m.format(SHORT_TIME_FORMAT_24_HOUR))

    m.add('00:30')
  }

  return times
}

const times = generateTimes()

interface Props extends Omit<React.ComponentProps<typeof RHFInput>, 'type'> {
  value?: string
  hasIcon?: boolean
  showClear?: boolean
  testIdPrefix?: string
}

const TimeInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      onChange,
      onBlur,
      onFocus,
      name,
      hasIcon = true,
      showClear = true,
      testIdPrefix,
      ...rest
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const intl = useIntl()

    useImperativeHandle(ref, () => inputRef.current!)

    const [isOpen, setIsOpen] = useState(false)
    const { watch, setValue } = useFormContext()
    const time = watch(name)

    const isMobileOS = useIsMobileDevice()

    const listRef = useRef<HTMLDivElement | null>(null)

    const onSelect = useCallback(
      (time: string) => {
        setValue(name, time, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
        setIsOpen(false)
      },
      [setValue, name],
    )

    const openDropdown = useCallback(() => {
      setIsOpen(true)

      const selectedTime = time ? moment(time, SHORT_TIME_FORMAT_24_HOUR) : moment()
      const preselectedTime = selectedTime
        .minutes(selectedTime.minutes() % 30 === 0 ? 0 : 30) // Round to nearest 30 minutes
        .format(SHORT_TIME_FORMAT_24_HOUR)

      const currentItem = listRef.current?.querySelector(
        `[data-time="${preselectedTime}"]`,
      )

      if (currentItem) {
        currentItem.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
        })
      }
    }, [time])

    const handleOnClick: MouseEventHandler<HTMLInputElement> =
      useCallback(() => {
        if (isSafari) {
          openDropdown()
        }
      }, [openDropdown])

    const handleOnFocus: FocusEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        if (!isSafari) {
          openDropdown()
        }
        onFocus?.(e)
      },
      [onFocus, openDropdown],
    )

    const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      (e) => {
        onChange?.(e)
      },
      [onChange],
    )

    const onDropdownClose = useCallback(() => {
      setIsOpen(false)
    }, [])

    const clear: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> =
      useCallback(
        (e) => {
          e.preventDefault()
          e.stopPropagation()
          setValue(name, null, {
            shouldTouch: true,
            shouldValidate: true,
          })
        },
        [setValue, name],
      )

    const formatTime = useCallback((time: string) => {
      const [hours, minutes] = time.split(':').map((s) => +s)
      const date = new Date()
      date.setHours(hours, minutes, 0, 0)
      return intlDateFormatter(date, INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)
    }, [intl.locale])

    return (
      <>
        <div>
          <RHFInput
            {...rest}
            ref={inputRef}
            name={name}
            type="time"
            onChange={handleOnChange}
            onFocus={handleOnFocus}
            onClick={handleOnClick}
            startIcon={hasIcon ? <LineClockIcon /> : undefined}
            endIcon={
              time &&
              showClear && (
                <IconButton kind="tertiary" variant="ghost" onClick={clear}>
                  <LineTimesIcon colour="normal-contrast" />
                </IconButton>
              )
            }
            data-testid={testIdPrefix && `${testIdPrefix}-input`}
          />
        </div>
        {!isMobileOS && (
          <DropdownList
            size="S"
            open={isOpen}
            triggerRef={inputRef}
            anchorRef={inputRef}
            onClose={onDropdownClose}
            placement="bottom-start"
            bodyRef={listRef}
            data-testid={testIdPrefix && `${testIdPrefix}-dropdown`}
          >
            {times.map((generatedTime) => (
              <ClickableListItem
                title={formatTime(generatedTime)}
                key={generatedTime}
                selected={generatedTime === time}
                data-time={generatedTime}
                onClick={() => onSelect(generatedTime)}
              />
            ))}
          </DropdownList>
        )}
      </>
    )
  },
)

TimeInput.displayName = 'TimeInput'

export default TimeInput
